import { createContext, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  deleteCookie,
  getJsonCookie,
  hasOptionalCookieConsent,
  setJsonCookie,
} from "../utils/cookies";

const SHARE_LIST_COOKIE_KEY = "imovelList";

interface ImovelShare {
  id: string;
  id_display?: string;
  nome?: string;
  image?: string;
}

interface ShareListContextProps {
  imovelList: ImovelShare[];
  addImovel: (
    id_imovel_display: string,
    id_imovel: string,
    nome_imovel: string,
    image: string,
  ) => Promise<void>;
  removeImovel: (id_imovel: string) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const ShareListContext = createContext<ShareListContextProps>(
  {} as ShareListContextProps,
);

const ShareListProvider = ({ children }: any) => {
  const [imovelList, setImovelList] = useState<ImovelShare[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!hasOptionalCookieConsent()) {
      return;
    }

    const savedImovelList = getJsonCookie<ImovelShare[]>(SHARE_LIST_COOKIE_KEY);
    if (savedImovelList) {
      setImovelList(savedImovelList);
    }
  }, []);

  const addImovel = async (
    id_imovel_display: string,
    id_imovel: string,
    nome_imovel?: string,
    image?: string,
  ) => {
    if (imovelList.length < 1) {
      setIsOpen(true);
    }
    const imovelExiste = imovelList.some((imovel) => imovel.id === id_imovel);
    if (imovelExiste) {
      return;
    }

    const nextList = [
      ...imovelList,
      {
        id: id_imovel,
        id_display: id_imovel_display,
        nome: nome_imovel,
        image: image,
      },
    ];
    setImovelList(nextList);

    if (hasOptionalCookieConsent()) {
      setJsonCookie(SHARE_LIST_COOKIE_KEY, nextList);
    }
  };

  const removeImovel = useCallback(
    (id_imovel: string) => {
      if (imovelList.length === 1) {
        setIsOpen(false);
      }
      const nextList = imovelList.filter((imovel) => imovel.id !== id_imovel);
      setImovelList(nextList);

      if (hasOptionalCookieConsent()) {
        if (nextList.length > 0) {
          setJsonCookie(SHARE_LIST_COOKIE_KEY, nextList);
        } else {
          deleteCookie(SHARE_LIST_COOKIE_KEY);
        }
      }

      toast.info("Imóvel removido da lista", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    },
    [imovelList],
  );

  return (
    <ShareListContext.Provider
      value={{ imovelList, addImovel, removeImovel, isOpen, setIsOpen }}
    >
      {children}
    </ShareListContext.Provider>
  );
};

export { ShareListContext, ShareListProvider };
